import { useState, useCallback } from "react";
import { collection, addDoc, doc, setDoc, deleteDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject, ref, listAll } from "firebase/storage";
import { db, storage, FIREBASE_READY, STORAGE_READY } from "../../firebase";
import { useApp } from "../../contexts/AppContext";
import { CLAN_DATA, ALL_FAMILIES, TOTAL_FAMILIES } from "../../data/landownerData";
import { handleImageUpload } from "../../utils/imageUpload";

/* ── helpers ──────────────────────────────────────────────────────── */
function initials(name) {
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}

const BLANK_FORM = { name: "", photo: null };

function storageErrMsg(e) {
  const code = e?.code ?? "";
  if (code === "storage/unauthorized" || code === "storage/unauthenticated")
    return "Permission denied — make sure you are signed in as admin.";
  if (code === "storage/quota-exceeded")   return "Firebase Storage quota exceeded.";
  if (code === "storage/bucket-not-found") return "Storage bucket not found.";
  return `Upload failed (${code || e?.message || "unknown error"}).`;
}

/* ── Component ────────────────────────────────────────────────────── */
export default function LandownersTab({ flash, flashError, uploading, setUploading }) {
  const { landowners } = useApp();

  /* navigation */
  const [selClanId, setSelClanId] = useState(CLAN_DATA[0].id);
  const [selSubId,  setSelSubId]  = useState(CLAN_DATA[0].subclans[0].id);
  const [selFamily, setSelFamily] = useState(CLAN_DATA[0].subclans[0].families[0]);

  /* add form */
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);

  /* edit state */
  const [editId,   setEditId]   = useState(null);
  const [editForm, setEditForm] = useState(BLANK_FORM);

  /* search */
  const [search, setSearch] = useState("");

  /* photo management */
  const [photoLoading, setPhotoLoading] = useState(null); /* member id with photo op in progress */

  /* ── derived ───────────────────────────────────────────────────── */
  const curClan = CLAN_DATA.find(c => c.id === selClanId);
  const curSub  = curClan.subclans.find(s => s.id === selSubId);

  const membersFor = useCallback((family, clanId) =>
    landowners.filter(m => m.family === family && m.clanId === clanId),
  [landowners]);

  const curMembers = membersFor(selFamily, selClanId);

  const familiesWithCount = curSub.families.map(f => ({
    name:  f,
    count: membersFor(f, selClanId).length,
  }));

  const searchResults = search.trim().length >= 2
    ? landowners.filter(m =>
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.family.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const familiesCovered = CLAN_DATA.reduce((acc, c) =>
    acc + c.subclans.reduce((a2, s) =>
      a2 + s.families.filter(f => membersFor(f, c.id).length > 0).length, 0), 0);

  /* ── photo upload helper ────────────────────────────────────────── */
  /* Generates a name-based filename: "Franco_Abal.jpeg", "Franco_Abal_02.jpeg" etc. */
  const resolveFileName = async (memberName, fileType) => {
    const ext  = fileType === "image/png"  ? "png"
               : fileType === "image/webp" ? "webp"
               : "jpeg";
    const base = memberName.trim().replace(/\s+/g, "_");
    const primary = `${base}.${ext}`;
    try {
      const existing = await listAll(storageRef(storage, "landowners"));
      const names = existing.items.map(i => i.name);
      if (!names.includes(primary)) return primary;
      for (let n = 2; n <= 99; n++) {
        const candidate = `${base}_${String(n).padStart(2, "0")}.${ext}`;
        if (!names.includes(candidate)) return candidate;
      }
    } catch (_) { /* if listing fails, fall back to primary */ }
    return primary;
  };

  const uploadPhoto = async (file, memberName) => {
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
      flashError("Please select an image file (JPEG, PNG, or WebP).");
      return null;
    }
    if (FIREBASE_READY && STORAGE_READY) {
      if (file.size > 3 * 1024 * 1024) {
        flashError("Image is too large — please use a file under 3 MB.");
        return null;
      }
      setUploading(true);
      try {
        const fileName = await resolveFileName(memberName, file.type);
        const path = `landowners/${fileName}`;
        const sr = storageRef(storage, path);
        await uploadBytes(sr, file, { contentType: file.type });
        return await getDownloadURL(sr);
      } catch (e) {
        console.error("Landowner photo upload error:", e);
        flashError(storageErrMsg(e));
        return null;
      } finally {
        setUploading(false);
      }
    } else {
      /* fall back to base64 */
      return await new Promise(res => handleImageUpload(file, res, 3));
    }
  };

  /* ── Add member ─────────────────────────────────────────────────── */
  const handleAdd = async () => {
    if (!addForm.name.trim()) { flashError("Please enter a name."); return; }
    const lineage = ALL_FAMILIES.find(f => f.family === selFamily && f.clanId === selClanId);
    if (!lineage) return;

    let photoUrl = null;
    if (addForm._file) {
      photoUrl = await uploadPhoto(addForm._file, addForm.name.trim());
      if (addForm._file && photoUrl === null) return; /* upload failed */
    }

    const record = {
      name:    addForm.name.trim(),
      clan:    curClan.name,
      clanId:  selClanId,
      subclan: curSub.name,
      subId:   selSubId,
      family:  selFamily,
      photo:   photoUrl,
    };

    try {
      if (FIREBASE_READY) {
        await addDoc(collection(db, "landowners"), record);
      } else {
        flashError("Firebase not configured — cannot save landowner records.");
        return;
      }
      setAddForm(BLANK_FORM);
      setAddOpen(false);
      flash(`${record.name} added to ${selFamily} family.`);
    } catch (e) {
      console.error(e);
      flashError("Save failed: " + (e?.message ?? "unknown"));
    }
  };

  /* ── Edit member ────────────────────────────────────────────────── */
  const startEdit = (m) => {
    setEditId(m.id);
    setEditForm({ name: m.name, photo: m.photo, _file: null });
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim()) { flashError("Name is required."); return; }
    const member = landowners.find(m => m.id === editId);
    if (!member) return;

    let photoUrl = editForm.photo;
    if (editForm._file) {
      photoUrl = await uploadPhoto(editForm._file, editForm.name.trim());
      if (photoUrl === null) return;
    }

    try {
      await setDoc(doc(db, "landowners", editId), { ...member, name: editForm.name.trim(), photo: photoUrl }, { merge: true });
      setEditId(null);
      flash(`${editForm.name.trim()} updated.`);
    } catch (e) {
      flashError("Update failed: " + (e?.message ?? "unknown"));
    }
  };

  /* ── Add / Change photo ─────────────────────────────────────────── */
  const handlePhotoChange = async (member, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { flashError("Please select an image file (JPEG, PNG, or WebP)."); return; }
    if (file.size > 3 * 1024 * 1024)    { flashError("Image is too large — max 3 MB."); return; }
    setPhotoLoading(member.id);
    try {
      /* Delete old photo from Storage before uploading new one */
      if (member.photo && member.photo.includes("firebasestorage")) {
        try { await deleteObject(ref(storage, member.photo)); } catch (_) {}
      }
      const url = await uploadPhoto(file, member.name);
      if (!url) return;
      await setDoc(doc(db, "landowners", member.id), { ...member, photo: url }, { merge: true });
      flash(`📷 Photo updated for ${member.name}.`);
    } catch (e) {
      flashError("Photo update failed: " + (e?.message ?? "unknown"));
    } finally {
      setPhotoLoading(null);
    }
  };

  /* ── Remove photo ────────────────────────────────────────────────── */
  const handlePhotoRemove = async (member) => {
    if (!member.photo) return;
    if (!window.confirm(`Remove photo for "${member.name}"?\n\nThe file will also be deleted from Storage.`)) return;
    setPhotoLoading(member.id);
    try {
      if (member.photo.includes("firebasestorage")) {
        try { await deleteObject(ref(storage, member.photo)); } catch (_) {}
      }
      await setDoc(doc(db, "landowners", member.id), { ...member, photo: null }, { merge: true });
      flash(`🗑 Photo removed for ${member.name}.`);
    } catch (e) {
      flashError("Photo removal failed: " + (e?.message ?? "unknown"));
    } finally {
      setPhotoLoading(null);
    }
  };

  /* ── Delete member ──────────────────────────────────────────────── */
  const handleDelete = async (member) => {
    if (!window.confirm(`Remove "${member.name}" from ${member.family} family?\n\nThis cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, "landowners", member.id));
      /* optionally delete the photo from Storage */
      if (member.photo && member.photo.includes("firebasestorage")) {
        try { await deleteObject(ref(storage, member.photo)); } catch (_) { /* ignore */ }
      }
      flash(`${member.name} removed.`);
      if (editId === member.id) setEditId(null);
    } catch (e) {
      flashError("Delete failed: " + (e?.message ?? "unknown"));
    }
  };

  /* ── Photo file input helper ─────────────────────────────────────── */
  const PhotoInput = ({ value, onChange, disabled }) => (
    <label style={{ cursor: disabled ? "wait" : "pointer", flexShrink: 0 }}>
      <div className="lo-admin-ring">
        {value
          ? <img src={value} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:"50%" }}/>
          : <span style={{ fontSize:"1.4rem" }}>📷</span>
        }
      </div>
      <input type="file" accept="image/jpeg,image/png,image/webp"
        style={{ display:"none" }} disabled={disabled}
        onChange={e => { onChange(e.target.files[0]); e.target.value = ""; }}
      />
    </label>
  );

  /* ── Navigate helpers ────────────────────────────────────────────── */
  const selectClan = (id) => {
    setSelClanId(id);
    const clan = CLAN_DATA.find(c => c.id === id);
    setSelSubId(clan.subclans[0].id);
    setSelFamily(clan.subclans[0].families[0]);
    setAddOpen(false); setEditId(null);
  };
  const selectSub = (id) => {
    setSelSubId(id);
    const sub = curClan.subclans.find(s => s.id === id);
    setSelFamily(sub.families[0]);
    setAddOpen(false); setEditId(null);
  };
  const selectFamily = (f) => {
    setSelFamily(f);
    setAddOpen(false); setEditId(null);
  };

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div>
      {/* ── Stats ─────────────────────────────────────────────────── */}
      <div className="acard">
        <div className="atitle">👥 Registered Landowners</div>
        <div className="stat-grid">
          {[
            { num: landowners.length,                               lbl: "Total Members" },
            { num: landowners.filter(m => m.photo).length,         lbl: "With Photos"   },
            { num: familiesCovered,                                  lbl: `Families Active` },
            { num: TOTAL_FAMILIES,                                   lbl: "Total Families" },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-num">{s.num}</div>
              <div className="stat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Search ────────────────────────────────────────────────── */}
      <div className="acard" style={{ paddingBottom: searchResults.length ? "1.2rem" : "1.75rem" }}>
        <div className="atitle" style={{ marginBottom: ".8rem" }}>🔍 Search Members</div>
        <input
          className="lo-search"
          placeholder="Search by name or family name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search.trim().length >= 2 && (
          <div style={{ marginTop: ".85rem" }}>
            {searchResults.length === 0
              ? <div style={{ color:"var(--gr)", fontSize:".87rem" }}>No members found for "{search}".</div>
              : (
                <div className="lo-search-results">
                  {searchResults.map(m => (
                    <div key={m.id} className="lo-search-row">
                      <div className="lo-sr-photo">
                        {m.photo
                          ? <img src={m.photo} alt={m.name}/>
                          : <div className="lo-sr-init">{initials(m.name)}</div>
                        }
                      </div>
                      <div>
                        <div style={{ fontWeight:700, fontSize:".88rem" }}>{m.name}</div>
                        <div style={{ fontSize:".75rem", color:"var(--gr)" }}>
                          {m.clan} · {m.subclan} · <strong>{m.family}</strong> family
                        </div>
                      </div>
                      <button className="btn-b" style={{ marginLeft:"auto", fontSize:".72rem", padding:".28rem .7rem" }}
                        onClick={() => { selectClan(m.clanId); selectSub(m.subId); selectFamily(m.family); setSearch(""); }}>
                        Go to family
                      </button>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}
      </div>

      {/* ── Navigation ────────────────────────────────────────────── */}
      <div className="acard">
        {/* Clan selector */}
        <div style={{ marginBottom: "1.2rem" }}>
          <div className="lo-nav-label">Clan</div>
          <div className="lo-pill-row">
            {CLAN_DATA.map(c => (
              <button key={c.id}
                className={`lo-pill${selClanId === c.id ? " on" : ""}`}
                onClick={() => selectClan(c.id)}>
                {c.name}
                <span className="lo-pill-sub">{c.tribe}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Subclan selector */}
        <div style={{ marginBottom: "1.2rem" }}>
          <div className="lo-nav-label">Subclan</div>
          <div className="lo-pill-row">
            {curClan.subclans.map(s => (
              <button key={s.id}
                className={`lo-pill${selSubId === s.id ? " on" : ""}`}
                onClick={() => selectSub(s.id)}>
                {s.name}
                <span className="lo-pill-sub">{s.families.length} families</span>
              </button>
            ))}
          </div>
        </div>

        {/* Family selector */}
        <div>
          <div className="lo-nav-label">Family</div>
          <div className="lo-family-grid">
            {familiesWithCount.map(({ name, count }) => (
              <button key={name}
                className={`lo-fam-btn${selFamily === name ? " on" : ""}${count > 0 ? " has-members" : ""}`}
                onClick={() => selectFamily(name)}>
                {name}
                {count > 0 && <span className="lo-fam-badge">{count}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Selected family members ──────────────────────────────── */}
      <div className="acard">
        <div className="atitle">
          <span>
            {selFamily} Family
            <span style={{ fontSize:".75rem", fontWeight:400, color:"var(--gr)", marginLeft:".5rem" }}>
              {curClan.name} · {curSub.name}
            </span>
          </span>
          <button className="btn-g" style={{ fontSize:".78rem", padding:".38rem .9rem" }}
            onClick={() => { setAddOpen(o => !o); setEditId(null); }}>
            {addOpen ? "✕ Cancel" : "+ Add Member"}
          </button>
        </div>

        {/* Add form */}
        {addOpen && (
          <div className="lo-member-form">
            <PhotoInput
              value={addForm._file ? URL.createObjectURL(addForm._file) : addForm.photo}
              onChange={file => setAddForm(f => ({ ...f, _file: file }))}
              disabled={uploading}
            />
            <div style={{ flex: 1, display:"flex", flexDirection:"column", gap:".5rem" }}>
              <input
                className="lo-name-input"
                placeholder="Full name (e.g. John Abal)"
                value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <div style={{ display:"flex", gap:".5rem" }}>
                <button className="btn-g" style={{ flex:1 }} onClick={handleAdd} disabled={uploading}>
                  {uploading ? "Uploading…" : "Save Member"}
                </button>
                <button className="btn-gray" onClick={() => { setAddOpen(false); setAddForm(BLANK_FORM); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Member grid */}
        {curMembers.length === 0 && !addOpen ? (
          <div className="empty-state">
            No members registered for {selFamily} family yet.<br/>
            <span style={{ fontSize:".82rem" }}>Click "+ Add Member" to register the first member.</span>
          </div>
        ) : (
          <div className="lo-member-grid">
            {curMembers.map(m => (
              <div key={m.id} className="lo-member-card">
                {editId === m.id ? (
                  /* Inline edit */
                  <div className="lo-edit-panel">
                    <PhotoInput
                      value={editForm._file ? URL.createObjectURL(editForm._file) : editForm.photo}
                      onChange={file => setEditForm(f => ({ ...f, _file: file }))}
                      disabled={uploading}
                    />
                    <input className="lo-name-input" value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && handleEditSave()}
                      style={{ marginTop:".4rem" }}
                    />
                    <div style={{ display:"flex", gap:".35rem", marginTop:".4rem" }}>
                      <button className="btn-g"    style={{ flex:1, fontSize:".72rem", padding:".3rem" }} onClick={handleEditSave} disabled={uploading}>
                        {uploading ? "…" : "Save"}
                      </button>
                      <button className="btn-gray" style={{ flex:1, fontSize:".72rem", padding:".3rem" }} onClick={() => setEditId(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="lo-member-photo">
                      {m.photo
                        ? <img src={m.photo} alt={m.name}/>
                        : <div className="lo-member-initials">{initials(m.name)}</div>
                      }
                    </div>

                    {/* ── Photo actions ── */}
                    <div style={{ display:"flex", gap:".3rem", justifyContent:"center", margin:".3rem 0 .1rem" }}>
                      {/* Add / Change photo */}
                      <label style={{ cursor: (photoLoading === m.id || uploading) ? "wait" : "pointer" }}
                        title={m.photo ? "Change photo" : "Add photo"}>
                        <span
                          className="btn-b"
                          style={{ fontSize:".65rem", padding:".2rem .55rem", display:"inline-block",
                                   opacity: (photoLoading === m.id || uploading) ? .5 : 1 }}>
                          {photoLoading === m.id ? "…" : m.photo ? "📷 Change" : "📷 Add"}
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          style={{ display:"none" }}
                          disabled={!!photoLoading || uploading}
                          onChange={e => { handlePhotoChange(m, e.target.files[0]); e.target.value = ""; }}
                        />
                      </label>

                      {/* Remove photo — only shown when photo exists */}
                      {m.photo && (
                        <button
                          className="btn-r"
                          style={{ fontSize:".65rem", padding:".2rem .55rem" }}
                          title="Remove photo"
                          disabled={!!photoLoading || uploading}
                          onClick={() => handlePhotoRemove(m)}>
                          🗑 Remove
                        </button>
                      )}
                    </div>

                    <div className="lo-member-name" title={m.name}>{m.name}</div>
                    <div className="lo-member-acts">
                      <button className="btn-b" style={{ fontSize:".68rem", padding:".22rem .55rem" }}
                        onClick={() => startEdit(m)}>✏️ Edit</button>
                      <button className="btn-r" style={{ fontSize:".68rem", padding:".22rem .55rem" }}
                        onClick={() => handleDelete(m)}>✕</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── All families overview ─────────────────────────────────── */}
      <div className="acard">
        <div className="atitle">All Families Overview</div>
        {CLAN_DATA.map(clan => (
          <div key={clan.id} style={{ marginBottom:"1.5rem" }}>
            <div className="lo-clan-header"
              style={{ background: clan.id === "kaiyaul" ? "linear-gradient(90deg,var(--gd),var(--g))" : "linear-gradient(90deg,var(--bd),var(--b))" }}>
              {clan.name} — {clan.tribe}
              <span style={{ opacity:.75, fontWeight:400, fontSize:".78rem", marginLeft:".5rem" }}>
                📍 {clan.location}
              </span>
            </div>
            {clan.subclans.map(sub => {
              const rows = sub.families.map(f => ({ f, count: membersFor(f, clan.id).length }));
              const total = rows.reduce((a, r) => a + r.count, 0);
              return (
                <div key={sub.id} style={{ marginLeft:"1rem", marginBottom:".85rem" }}>
                  <div className="lo-sub-row">
                    <span style={{ fontWeight:700 }}>{sub.name}</span>
                    <span style={{ color:"var(--gr)", fontSize:".78rem", marginLeft:".5rem" }}>
                      {rows.filter(r=>r.count>0).length}/{sub.families.length} families active · {total} members
                    </span>
                  </div>
                  <div className="lo-family-grid" style={{ marginTop:".4rem" }}>
                    {rows.map(({ f, count }) => (
                      <button key={f}
                        className={`lo-fam-btn${count > 0 ? " has-members" : ""}${selFamily === f && selClanId === clan.id ? " on" : ""}`}
                        onClick={() => { selectClan(clan.id); selectSub(sub.id); selectFamily(f); window.scrollTo({ top:0, behavior:"smooth" }); }}>
                        {f}
                        {count > 0 && <span className="lo-fam-badge">{count}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
